const quickAddProductCache = new Map();

function fetchQuickAddProduct(url) {
  if (!quickAddProductCache.has(url)) {
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Quick Choose request failed: ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        quickAddProductCache.delete(url);
        throw error;
      });

    quickAddProductCache.set(url, request);
  }

  return quickAddProductCache.get(url);
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.prefetchReady) return;

        const modalSelector = `#${this.id}`;
        const modalOpener = Array.from(document.querySelectorAll('modal-opener')).find(
          (element) => element.getAttribute('data-modal') === modalSelector
        );
        const opener = modalOpener?.querySelector('[data-product-url]');
        if (!opener) return;

        this.prefetchReady = true;
        const prefetch = () => fetchQuickAddProduct(opener.getAttribute('data-product-url')).catch(() => {});
        opener.addEventListener('pointerenter', prefetch, { once: true, passive: true });
        opener.addEventListener('focus', prefetch, { once: true, passive: true });
        opener.addEventListener('touchstart', prefetch, { once: true, passive: true });
      }

      hide(preventFocus = false) {
        if (this.classList.contains('is-closing') || !this.hasAttribute('open')) return;
        this.loadRequestId = (this.loadRequestId || 0) + 1;
        this.classList.remove('is-loading');
        this.pickerExpansionController?.abort();

        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        if (preventFocus) this.openedBy = null;

        if (!this.classList.contains('quick-add-modal--drawer')) {
          super.hide();
          document.body.classList.remove('quick-add-modal-open');
          this.classList.remove('is-picker-expanded');
          this.modalContent.innerHTML = '';
          return;
        }

        const finishClosing = () => {
          if (!this.classList.contains('is-closing')) return;
          this.classList.remove('is-closing');
          super.hide();
          document.body.classList.remove('quick-add-modal-open');
          this.classList.remove('is-picker-expanded');
          this.modalContent.innerHTML = '';
        };

        this.classList.add('is-closing');

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          finishClosing();
          return;
        }

        const drawer = this.querySelector('.quick-add-modal__content');
        const onDrawerTransitionEnd = (event) => {
          if (event.target !== drawer || event.propertyName !== 'transform') return;
          drawer.removeEventListener('transitionend', onDrawerTransitionEnd);
          finishClosing();
        };
        drawer.addEventListener('transitionend', onDrawerTransitionEnd);
        window.setTimeout(finishClosing, 580);
      }

      show(opener) {
        this.classList.remove('is-closing');
        this.classList.remove('is-picker-expanded');
        this.classList.add('is-loading');
        document.body.classList.add('quick-add-modal-open');
        const requestId = (this.loadRequestId || 0) + 1;
        this.loadRequestId = requestId;
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        super.show(opener);

        fetchQuickAddProduct(opener.getAttribute('data-product-url'))
          .then((responseText) => {
            if (
              requestId !== this.loadRequestId ||
              !this.hasAttribute('open') ||
              this.classList.contains('is-closing')
            )
              return;

            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();
            this.setupMobilePickerExpansion();

          })
          .catch((error) => {
            console.error(error);
            if (requestId === this.loadRequestId && this.hasAttribute('open')) this.hide();
          })
          .finally(() => {
            if (requestId === this.loadRequestId) this.classList.remove('is-loading');
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setupMobilePickerExpansion() {
        this.pickerExpansionController?.abort();
        const mobileQuery = window.matchMedia('(max-width: 749px)');
        const picker = this.modalContent.querySelector('product-color-quantity-picker');
        const drawer = this.querySelector('.quick-add-modal__content');
        if (!mobileQuery.matches || !picker || !drawer) return;

        this.pickerExpansionController = new AbortController();
        let animationFrame = 0;
        const checkPickerPosition = () => {
          animationFrame = 0;
          if (!this.hasAttribute('open') || this.classList.contains('is-picker-expanded')) return;
          const pickerTop = picker.getBoundingClientRect().top;
          const drawerTop = drawer.getBoundingClientRect().top;
          if (this.modalContent.scrollTop > 0 && pickerTop <= drawerTop + 2) {
            this.classList.add('is-picker-expanded');
            this.pickerExpansionController?.abort();
          }
        };

        this.modalContent.addEventListener(
          'scroll',
          () => {
            if (animationFrame) return;
            animationFrame = requestAnimationFrame(checkPickerPosition);
          },
          { passive: true, signal: this.pickerExpansionController.signal }
        );
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
